import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Image, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Logo from '../components/Logo';
import LogoMark from '../components/LogoMark';
import Text from '../components/ui/Text';
import {
  BASE_WIDTH,
  colors,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * Splash: the supplied room photograph, the full lockup centred, and the
 * brand line along the bottom behind a petal divider.
 */

const TAGLINE = 'Premium home services,\nthoughtfully delivered.';

/** The five service marks that circle the lockup. */
const ORBIT_ICONS = [
  require('../../assets/uploads/splash/icon_spray.png'),
  require('../../assets/uploads/splash/icon_roller.png'),
  require('../../assets/uploads/splash/icon_tap.png'),
  require('../../assets/uploads/splash/icon_plug.png'),
  require('../../assets/uploads/splash/icon_washer.png'),
];

/**
 * Ring radius as a share of the screen width. Comfortably outside the lockup,
 * which reaches `LOCKUP_RATIO / 2` (0.22) from the centre.
 */
const ORBIT_RATIO = 0.36;

/**
 * The same, against the *height* - and the reason this screen survives a
 * landscape phone.
 *
 * The ring is a circle, so the axis that constrains it is whichever one is
 * shorter, and sizing it off the width alone only ever asked the wider one. On
 * an 844x390 phone on its side that read 0.36 x 844 = a 608pt ring on a 390pt
 * screen: the twelve and six o'clock icons were simply off the display, and
 * the tagline underneath was buried in the middle of it.
 *
 * 0.28 means the ring is never taller than 56% of the screen, which leaves the
 * footer its own room. On any phone held upright the width term below is far
 * smaller and this one never binds - the reference device is untouched.
 */
const ORBIT_HEIGHT_RATIO = 0.28;

/**
 * Ceiling on the radius, in points.
 *
 * Past this the ring stops being a formation around a logo and becomes the
 * screen's border. A tablet has the room for a 400pt ring; it does not follow
 * that it wants a 740pt one, which is what the raw ratio asks for at 1024pt
 * wide. More width should buy more air around the lockup, not a bigger circle.
 */
const ORBIT_MAX_RADIUS = 200;

/** Icon size on the reference device; scaled with the ring below. */
const ORBIT_ICON = 54;

/** The radius the reference phone produces - what icon scaling is measured from. */
const REFERENCE_ORBIT_RADIUS = BASE_WIDTH * ORBIT_RATIO;

/** One full turn. Slow enough to read as drift rather than spin. */
const SPIN_MS = 28000;

/**
 * Lockup width as a share of the screen, so it scales with the device.
 *
 * Held down deliberately. `logo_transparent.png` is only 215px wide, so on a
 * 3x screen anything past ~72pt is already upscaling and the wordmark starts
 * to soften. 0.44 keeps it near a 2x stretch, which reads clean; 0.62 was
 * visibly blurry. Raising this needs a higher-resolution logo file first.
 */
const LOCKUP_RATIO = 0.44;

/**
 * Half the lockup's width as a share of the ring's radius.
 *
 * The lockup is sized *from the ring* rather than from the screen, so the one
 * relationship that makes this composition read - a mark sitting comfortably
 * inside a circle of icons - holds at every size. Sizing both off the width
 * independently broke it the moment either hit a limit the other did not: on a
 * landscape phone the ring was clamped by the height while the lockup kept
 * growing with the width, and the wordmark ran out through the icons.
 *
 * The value is exactly `(LOCKUP_RATIO / 2) / ORBIT_RATIO`, i.e. the proportion
 * the two ratios already had on the reference device, so nothing moves there.
 */
const LOCKUP_OF_ORBIT = LOCKUP_RATIO / 2 / ORBIT_RATIO;

/**
 * Ceiling on the lockup's width, in points.
 *
 * This is a resolution limit, not a taste one - see `LOCKUP_RATIO`. The
 * artwork is 215px wide, and a tablet that scaled it with the ring would draw
 * it at ~245pt, well past the point where the wordmark visibly softens. It is
 * the one element here that gains nothing from a bigger screen.
 */
const LOCKUP_MAX_WIDTH = 200;

/** Intrinsic proportions of the lockup artwork. */
const LOCKUP_ASPECT = 215 / 135;

const ENTER_MS = 900;
/** The line follows the lockup in rather than arriving with it. */
const TAGLINE_DELAY = 260;

/**
 * Every size on this screen, for a given window.
 *
 * Pulled out of the component and exported because it is the part that was
 * actually wrong on anything but the reference phone, and a mounted test
 * cannot see it - nothing in the test renderer runs Yoga, so a smoke test that
 * "the splash renders on a landscape phone" passed happily while the ring hung
 * 100pt off both edges. As a pure function of two numbers it can simply be
 * asserted (see `__tests__/responsive.test.tsx`).
 */
export const splashGeometry = (width: number, height: number) => {
  /*
   * Whichever of the three limits bites first - the width it was drawn
   * against, the height it has to fit inside, and the point past which a ring
   * is just too big to read as one.
   *
   * On the reference 390x844 phone the width term wins at 140.4 and the other
   * two are nowhere near, so this screen renders exactly as it did before any
   * of it existed. The height term is what saves a phone on its side; the
   * ceiling is what saves a tablet.
   */
  const orbitRadius = Math.min(
    width * ORBIT_RATIO,
    height * ORBIT_HEIGHT_RATIO,
    ORBIT_MAX_RADIUS,
  );
  const orbitSize = orbitRadius * 2;

  /*
   * Icons follow the ring rather than staying at 54.
   *
   * They are decoration on a splash, not controls - nothing is pressed here -
   * so the usual "touch targets do not scale" rule does not apply, and a fixed
   * 54pt glyph on a 220pt ring is visibly heavy while the same glyph on a
   * 400pt one is lost. Damped and clamped so they never run to either extreme,
   * and exactly 54 on the reference device.
   */
  const orbitIcon = Math.round(
    ORBIT_ICON * clamp(orbitRadius / REFERENCE_ORBIT_RADIUS, 0.8, 1.25),
  );

  /*
   * Sized from the ring, so the mark keeps its place inside the circle on
   * every device; `Logo` takes a height, so convert through the artwork's own
   * aspect. Capped because the source file runs out of pixels before a tablet
   * runs out of room.
   */
  const lockupWidth = Math.min(orbitSize * LOCKUP_OF_ORBIT, LOCKUP_MAX_WIDTH);
  const lockupHeight = Math.round(lockupWidth / LOCKUP_ASPECT);

  return { orbitRadius, orbitSize, orbitIcon, lockupWidth, lockupHeight };
};

type Props = {
  /** Called once the splash has been held long enough to read. */
  onDone: () => void;
  duration?: number;
};

function SplashScreen({ onDone, duration = 2600 }: Props) {
  const resp = useResponsive();
  const { width, height } = resp;
  const insets = useSafeAreaInsets();

  const settle = useRef(new Animated.Value(0)).current;
  const line = useRef(new Animated.Value(0)).current;
  const spin = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // The ring turns for as long as the splash is up; the entrance plays once.
    const turning = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: SPIN_MS,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    turning.start();

    const entrance = Animated.parallel([
      Animated.timing(settle, {
        toValue: 1,
        duration: ENTER_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(line, {
        toValue: 1,
        delay: TAGLINE_DELAY,
        duration: ENTER_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]);
    entrance.start();

    const handoff = setTimeout(onDone, duration);
    return () => {
      clearTimeout(handoff);
      entrance.stop();
      turning.stop();
    };
  }, [duration, line, onDone, settle, spin]);

  const { orbitRadius, orbitSize, orbitIcon, lockupHeight } = splashGeometry(
    width,
    height,
  );

  /*
   * The ring rotates and each icon rotates back by the same amount, so the
   * formation turns while every glyph stays upright. Without the counter-turn
   * the spray bottle ends up on its head halfway round.
   */
  const turn = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });
  const counterTurn = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '-360deg'],
  });

  return (
    <View style={styles.root}>
      {/*
        `splash_wall.png` is the supplied artwork with its black letterbox
        bands trimmed (the original was 1080x1920 with ~186px of black top and
        bottom, which "cover" would have pulled onto a tall screen). It is also
        renamed off "splash .png" - a space in an asset filename survives Metro
        but is fragile through the Android resource pipeline.

        No scrim over it: unlike the previous photograph this one is already a
        pale, near-empty wall, and the 42% veil only greyed it out.
      */}
      <Image
        source={require('../../assets/uploads/splash/splash_wall.png')}
        style={[StyleSheet.absoluteFill, { width, height }]}
        resizeMode="cover"
      />

      <View style={styles.centre}>
        {/*
          The ring is a normal flex child so the column centres it, and the
          lockup is laid over the same box. Both therefore share a centre
          point without either having to measure the other.
        */}
        <Animated.View
          style={[
            styles.ring,
            {
              width: orbitSize,
              height: orbitSize,
              opacity: settle,
              transform: [
                { rotate: turn },
                {
                  scale: settle.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.78, 1],
                  }),
                },
              ],
            },
          ]}
          pointerEvents="none"
        >
          {ORBIT_ICONS.map((icon, i) => {
            // Even share of the circle, starting at twelve o'clock.
            const angle = (i / ORBIT_ICONS.length) * 2 * Math.PI - Math.PI / 2;

            return (
              <Animated.View
                key={i}
                style={[
                  styles.orbiter,
                  {
                    width: orbitIcon,
                    height: orbitIcon,
                    left:
                      orbitRadius + Math.cos(angle) * orbitRadius - orbitIcon / 2,
                    top:
                      orbitRadius + Math.sin(angle) * orbitRadius - orbitIcon / 2,
                    transform: [{ rotate: counterTurn }],
                  },
                ]}
              >
                <Image source={icon} style={styles.orbiterArt} resizeMode="contain" />
              </Animated.View>
            );
          })}
        </Animated.View>

        <View style={styles.lockup} pointerEvents="none">
          <Animated.View
            style={{
              opacity: settle,
              transform: [
                {
                  scale: settle.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.9, 1],
                  }),
                },
              ],
            }}
          >
            <Logo height={lockupHeight} />
          </Animated.View>
        </View>
      </View>

      <Animated.View
        style={[
          styles.footer,
          /*
           * The tagline was landing on the navigation bar. The inset alone is
           * not enough here - this is the last thing on the screen and wants
           * to read as deliberately placed, not pushed to the edge.
           *
           * The 64pt lift is a tall screen's luxury. On a short one it is what
           * pushes the line up off the bottom and into the ring, so there it
           * drops to a single step - still clear of the nav bar, which is the
           * part that was actually a bug, without climbing into the artwork.
           */
          {
            paddingBottom:
              Math.max(insets.bottom, spacing.xl) +
              (resp.isShort ? spacing.xl : spacing.xxxl * 2),
          },
          {
            opacity: line,
            transform: [
              {
                translateY: line.interpolate({
                  inputRange: [0, 1],
                  outputRange: [12, 0],
                }),
              },
            ],
          },
        ]}
      >
        {/* Hairline either side of a small petal, per the "hairline divides
            without decorating" rule - the mark is the only ornament. */}
        <View style={styles.divider}>
          <View style={styles.rule} />
          {/* Body ink rather than `borderStrong`, which vanished against the
              photograph. Small enough to punctuate the rule, not sit on it. */}
          <LogoMark size={14} color={colors.inkBody} />
          <View style={styles.rule} />
        </View>

        <Text variant="body" color={colors.inkBody} align="center">
          {TAGLINE}
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  centre: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    // Icons are placed by absolute offset inside it.
    position: 'relative',
  },
  orbiter: {
    position: 'absolute',
  },
  orbiterArt: {
    width: '100%',
    height: '100%',
  },
  /* Laid over the ring, centred on the same point. */
  lockup: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: screenPadding,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  rule: {
    width: 48,
    height: 1,
    // `border` is tuned for white cards; over the photograph it vanished.
    backgroundColor: colors.borderStrong,
  },
});

export default SplashScreen;
